import { add, addDays, isSameDay, isValid, startOfWeek } from 'date-fns';
import { useEffect, useState } from 'react';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import { WeekList } from './WeekList';
import styles from './DeliveriesWeekView.module.scss';
import { DragDropContext, DraggableLocation } from '@hello-pangea/dnd';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { normalizeDeliveryTicket } from 'helpers/planning';
import { Button, LinearProgress } from '@mui/material';
import classNames from 'classnames';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { convertDeliveryStatusToInternalName } from 'lib/constants';
import {
    useLazyUpdateDeliveryTicketQuery,
    useLazyUpdateResolvedDeliveryTicketQuery,
} from 'store/services/planning';
import { DeviceLookup } from 'views/Planning/Planning';

interface WeekViewProps {
    deliveries: Array<Ticket | null>;
    devicesList: DeviceLookup;
    selectedDate: Date;
    selectedTickets: string[];
    setSelectedTickets: (s: string[]) => void;
    changeWeek: (d: number) => void;
    setActiveTicket: (t: Ticket) => void;
    setIsBulkModalOpen: (o: boolean) => void;
    setAllDeliveries: (
        d: Ticket[] | ((deliveries: Ticket[]) => Ticket[])
    ) => void;
    isLoading?: boolean;
}

export const DeliveriesWeekView = ({
    deliveries,
    selectedDate,
    selectedTickets,
    setSelectedTickets,
    changeWeek,
    setActiveTicket,
    setIsBulkModalOpen,
    setAllDeliveries,
    devicesList,
    isLoading,
}: WeekViewProps) => {
    const [dayBuckets, setDayBuckets] = useState<
        Array<Array<Ticket | null> | null>
    >([null]);
    const [weekdays, setWeekdays] = useState<Array<Date>>([selectedDate]);
    const authentication = useSelector(
        (state: RootState) => state.authentication
    );

    const [draggingTicket, setDraggingTicket] = useState<string>('');
    const [isChangingTicketDate, setIsChangingTicketDate] = useState(false);

    const [weekChangeInterval, setWeekChangeInterval] = useState<
        undefined | number
    >();

    const [updateDeliveryQuery] = useLazyUpdateDeliveryTicketQuery();
    const [updateResolvedTicketQuery] =
        useLazyUpdateResolvedDeliveryTicketQuery();

    useEffect(() => {
        const firstDay = addDays(startOfWeek(selectedDate), 1);
        const newWeekdays = [];
        const newDayBuckets = [];
        for (let i = 0; i < 7; i++) {
            const today = add(firstDay, { days: i });
            const batch = deliveries.filter((ticket) => {
                if (!ticket) {
                    return false;
                }
                const device = devicesList[ticket.deviceId];
                // need to keep ticket that's being dragged so pangea can track it
                if (
                    draggingTicket &&
                    i === Number(draggingTicket.split('.')?.[1])
                ) {
                    if (ticket?.id === draggingTicket.split('.')?.[0]) {
                        return true;
                    }
                }

                if (
                    ticket?.dateDelivered &&
                    convertDeliveryStatusToInternalName[ticket.status] ===
                        'done'
                ) {
                    return isSameDay(new Date(ticket.dateDelivered), today);
                }

                if (ticket?.plannedDate) {
                    return isSameDay(new Date(ticket.plannedDate), today);
                }

                if (device?.duf_7) {
                    return isSameDay(new Date(device?.duf_7), today);
                }

                return false;
            });
            newWeekdays.push(today);
            newDayBuckets.push(batch);
        }
        setWeekdays(newWeekdays);
        if (newDayBuckets.length) {
            setDayBuckets(newDayBuckets);
        } else {
            setDayBuckets([null]);
        }
    }, [deliveries, selectedDate, draggingTicket, devicesList]);

    const onDragStart = (draggableId: string) => {
        setDraggingTicket(draggableId);
    };

    const onDragEnd = (
        destination: DraggableLocation | null,
        draggableId: string
    ) => {
        setDraggingTicket('');
        setIsChangingTicketDate(true);
        clearInterval(weekChangeInterval);
        if (!destination || !draggableId || !authentication.supplierId) {
            return;
        }

        const draggedTicket = deliveries.find(
            (ticket) => ticket?.id === draggableId.split('.')?.[0]
        );
        const innerRef = document.querySelector(
            `div[data-rfd-droppable-id="${destination.droppableId}"]`
        );
        if (!innerRef?.id) {
            return;
        }
        const targetDate = new Date(innerRef.id).valueOf();

        if (!draggedTicket || !isValid(targetDate)) {
            return;
        }

        const dateTypeToChange =
            convertDeliveryStatusToInternalName[draggedTicket.status] === 'done'
                ? 'dateDelivered'
                : 'plannedDate';

        const newTicket = normalizeDeliveryTicket({
            ...draggedTicket,
            [dateTypeToChange]: targetDate,
        });

        const updateRequest =
            newTicket.status === 'done'
                ? updateResolvedTicketQuery
                : updateDeliveryQuery;

        Promise.resolve(
            updateRequest({
                supplierId: authentication.supplierId,
                deviceId: newTicket.deviceId,
                newTicket,
            })
        )
            .then((result) => {
                if (result?.data) {
                    setAllDeliveries((oldDeliveries: Ticket[]) => {
                        if (!result?.data) {
                            return oldDeliveries;
                        }

                        const newDeliveries = [...oldDeliveries];

                        const index = newDeliveries.findIndex(
                            (delivery) => delivery?.id === result.data?.id
                        );

                        if (index > -1) {
                            newDeliveries[index] = result.data;
                        } else {
                            newDeliveries.push(result.data);
                        }

                        return newDeliveries;
                    });
                }
            })
            .finally(() => setIsChangingTicketDate(false));
    };

    return (
        <div className={styles.container}>
            <LinearProgress
                variant="indeterminate"
                sx={{
                    width: '100%',
                    display: isLoading ? 'default' : 'none',
                }}
            />
            <div className={styles.weekView} data-testid="DeliveriesWeekView">
                <div
                    className={classNames(styles.weekChangeZone, styles.left, {
                        [styles.show]: draggingTicket,
                    })}
                    onMouseEnter={() =>
                        setWeekChangeInterval(
                            window.setInterval(() => {
                                changeWeek(-1);
                            }, 2000)
                        )
                    }
                    onMouseLeave={() =>
                        window.clearInterval(weekChangeInterval)
                    }
                >
                    <ChevronLeft />
                </div>
                <DragDropContext
                    onDragEnd={({ destination, draggableId }) =>
                        onDragEnd(destination, draggableId)
                    }
                    onDragStart={({ draggableId }) => {
                        onDragStart(draggableId);
                    }}
                >
                    <WeekList
                        tickets={dayBuckets?.[0] ? dayBuckets[0] : null}
                        devicesList={devicesList}
                        date={weekdays[0]}
                        weekday={'Mon'}
                        setActiveTicket={setActiveTicket}
                        selectedTickets={selectedTickets}
                        setSelectedTickets={setSelectedTickets}
                        isLoading={isLoading || isChangingTicketDate}
                    />
                    <WeekList
                        tickets={dayBuckets?.[1] ? dayBuckets[1] : null}
                        devicesList={devicesList}
                        date={weekdays[1]}
                        weekday={'Tues'}
                        setActiveTicket={setActiveTicket}
                        selectedTickets={selectedTickets}
                        setSelectedTickets={setSelectedTickets}
                        isLoading={isLoading || isChangingTicketDate}
                    />
                    <WeekList
                        tickets={dayBuckets?.[2] ? dayBuckets[2] : null}
                        devicesList={devicesList}
                        date={weekdays[2]}
                        weekday={'Wed'}
                        setActiveTicket={setActiveTicket}
                        selectedTickets={selectedTickets}
                        setSelectedTickets={setSelectedTickets}
                        isLoading={isLoading || isChangingTicketDate}
                    />
                    <WeekList
                        tickets={dayBuckets?.[3] ? dayBuckets[3] : null}
                        devicesList={devicesList}
                        date={weekdays[3]}
                        weekday={'Thurs'}
                        setActiveTicket={setActiveTicket}
                        selectedTickets={selectedTickets}
                        setSelectedTickets={setSelectedTickets}
                        isLoading={isLoading || isChangingTicketDate}
                    />
                    <WeekList
                        tickets={dayBuckets?.[4] ? dayBuckets[4] : null}
                        devicesList={devicesList}
                        date={weekdays[4]}
                        weekday={'Fri'}
                        setActiveTicket={setActiveTicket}
                        selectedTickets={selectedTickets}
                        setSelectedTickets={setSelectedTickets}
                        isLoading={isLoading || isChangingTicketDate}
                    />
                    <WeekList
                        tickets={dayBuckets?.[5] ? dayBuckets[5] : null}
                        devicesList={devicesList}
                        date={weekdays[5]}
                        weekday={'Sat'}
                        setActiveTicket={setActiveTicket}
                        selectedTickets={selectedTickets}
                        setSelectedTickets={setSelectedTickets}
                        isLoading={isLoading || isChangingTicketDate}
                    />
                    <WeekList
                        tickets={dayBuckets?.[6] ? dayBuckets[6] : null}
                        devicesList={devicesList}
                        date={weekdays[6]}
                        weekday={'Sun'}
                        setActiveTicket={setActiveTicket}
                        selectedTickets={selectedTickets}
                        setSelectedTickets={setSelectedTickets}
                        isLoading={isLoading || isChangingTicketDate}
                    />
                </DragDropContext>
                <div
                    className={classNames(styles.weekChangeZone, styles.right, {
                        [styles.show]: draggingTicket,
                    })}
                    onMouseEnter={() =>
                        setWeekChangeInterval(
                            window.setInterval(() => changeWeek(1), 2000)
                        )
                    }
                    onMouseLeave={() => clearInterval(weekChangeInterval)}
                >
                    <ChevronRight />
                </div>
            </div>
            <div
                className={classNames(styles.footerActions, {
                    [styles.active]: selectedTickets.length,
                })}
            >
                <strong>Bulk Actions: </strong>
                <Button variant="text" onClick={() => setSelectedTickets([])}>
                    Clear
                </Button>
                <Button variant="text" onClick={() => setIsBulkModalOpen(true)}>
                    Update Status
                </Button>
            </div>
        </div>
    );
};
